import { useCallback, useEffect, useRef, useState } from 'react'
import { Pause, Play } from 'lucide-react'
import toast from 'react-hot-toast'

/**
 * Compact play/pause control for an audio attachment.
 *
 * Nothing is fetched until the user asks: the first click loads the file (a
 * spinner replaces the button meanwhile), then it starts playing on its own and
 * the button becomes a play/pause toggle.
 *
 * `load` is passed in rather than the URL so the bytes are only ever requested
 * on demand — a lead with many voice notes must not fetch all of them to render
 * a list.
 */
export function WaVoicePlayer({ load, title = 'Play' }: {
  load: () => Promise<string>
  title?: string
}) {
  const [url, setUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [playing, setPlaying] = useState(false)
  const audioRef = useRef<HTMLAudioElement>(null)

  const start = useCallback(async () => {
    if (loading) return
    setLoading(true)
    try {
      setUrl(await load())
    } catch {
      toast.error('Could not load the recording')
    } finally {
      setLoading(false)
    }
  }, [load, loading])

  // Autoplay once the bytes arrive. The click that started this is the user
  // gesture browsers require, but the await can outlive that window, so a
  // blocked play just leaves the button ready to be pressed again.
  useEffect(() => {
    if (!url) return
    audioRef.current?.play().catch(() => setPlaying(false))
  }, [url])

  const toggle = () => {
    const el = audioRef.current
    if (!el) return
    if (el.paused) el.play().catch(() => setPlaying(false))
    else el.pause()
  }

  if (loading) {
    return (
      <span
        className="btn btn-ghost btn-sm p-1.5 text-gray-400"
        title="Loading recording…"
        aria-label="Loading recording"
      >
        <span className="block h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-indigo-500" />
      </span>
    )
  }

  if (!url) {
    return (
      <button
        onClick={start}
        className="btn btn-ghost btn-sm p-1.5 text-gray-400 hover:text-indigo-600"
        title={title}
      >
        <Play className="h-3.5 w-3.5" />
      </button>
    )
  }

  return (
    <>
      <button
        onClick={toggle}
        className="btn btn-ghost btn-sm p-1.5 text-indigo-600 hover:text-indigo-700"
        title={playing ? 'Pause' : 'Play'}
      >
        {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
      </button>
      <audio
        ref={audioRef}
        src={url}
        className="hidden"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
    </>
  )
}
